import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { UpdateSettingsRequest } from '@/lib/api/invoice-api'
import { invoiceApi } from '@/lib/api/invoice-api'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'

export const invoiceKeys = {
    settings: () => ['invoice-settings'] as const,
}

export function useInvoiceSettings() {
    const { hasAnyPermission, arePermissionsLoaded } = useAuth()
    return useQuery({
        queryKey: invoiceKeys.settings(),
        queryFn: async () => {
            const res = await invoiceApi.getSettings()
            return res.data ?? null
        },
        // Wait for permissions to load before deciding: login() seeds an
        // empty permission set synchronously then background-refetches, so
        // gating on a not-yet-loaded set would wrongly disable the query for
        // a user who does have invoices.read. Người bán hàng (create/quick_create)
        // cũng đọc được để tính VAT — BE mở route tương ứng.
        enabled:
            arePermissionsLoaded &&
            hasAnyPermission(
                'invoices.read',
                'sales_orders.create',
                'sales_orders.quick_create',
                // Nhân viên kho in phiếu nhập kho cần tên/địa chỉ/logo HTX ở
                // đầu trang — BE đã mở 5 quyền đọc kho cho route này.
                'product_lots.read',
                'sorting_lots.read',
                'scenting_lots.read',
                'packaging_lots.read',
                'agri_material_lots.read',
            ),
    })
}

export function useUpdateInvoiceSettings() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpdateSettingsRequest) =>
            invoiceApi.updateSettings(data),
        onSuccess: (response) => {
            if (response.data) {
                qc.setQueryData(invoiceKeys.settings(), response.data)
            }
            void qc.invalidateQueries({ queryKey: invoiceKeys.settings() })
            toast.success(m.toast_invoice_update_success())
        },
        onError: () => toast.error(m.toast_invoice_update_error()),
    })
}
